import { Braces, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { errorMessage, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { isSettingsConflict, useBrandDefaultTexts, useEffectiveSettings, useSaveSettings, type SettingsDocuments, type SettingsKind } from "../api/queries";
import { OwnTextField } from "../components/OwnText";
import { ownTextError } from "../lib/ownText";
import { Alert, Button, Card, ChipsInput, Field, NumberInput, PageHead, Select, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { useIntlShape, useT, type Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { SetbackPresetPicker } from "./SetbackPresetPicker";
import { TravelSection } from "./RulesTravel";
import { travelProblems } from "../lib/travel";
import { useLeaveGuard } from "../lib/leaveGuard";

type Docs = SettingsDocuments;
type Tab = "roof" | "module" | "offer" | "area" | "advanced";

const KINDS: SettingsKind[] = ["setbacks", "layout", "roof_ai", "equipment", "production", "pricing", "incentives", "financial", "brand", "service_area"];
/** `roof_ai` → `rules.kind.roofAi`: the name of a settings document. */
const camel = (v: string) => v.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
const kindName = (t: Translate, kind: SettingsKind) => t(`rules.kind.${camel(kind)}` as MessageKey);
const materialName = (t: Translate, m: S["RoofMaterial"]) => t(`rules.material.${camel(m)}` as MessageKey);
const MATERIALS: S["RoofMaterial"][] = [
  "asphalt_shingle", "metal_standing_seam", "metal_exposed_fastener", "metal", "concrete_tile", "clay_tile", "flat_tile", "barrel_tile",
  "slate", "wood_shake", "sbs_torch_on", "epdm", "tpo", "built_up", "rubber_shingle", "lightweight_tile", "metal_interlocking", "other",
];

/** A local, editable copy of one settings kind with dirty tracking and save/discard. The copy remembers the version
 * it was loaded from: a save names it, and one made after someone else saved the same rules is refused (409) and
 * says so, with Reload, instead of putting their older values back. Newer rules from the server replace the copy
 * only while it has no unsaved edits. */
function useDraft<K extends SettingsKind>(orgId: string, docs: Docs | undefined, kind: K) {
  const settings = useEffectiveSettings(orgId);
  const versions = settings.data?.versions;
  const save = useSaveSettings(orgId);
  const toast = useToast();
  const t = useT();
  const latest = docs?.[kind] as Docs[K] | undefined;
  const latestVersion = versions?.[kind] ?? 0;
  const [base, setBase] = useState<{ doc: Docs[K]; version: number } | null>(null);
  const [draft, setDraft] = useState<Docs[K] | null>(null);
  const [conflict, setConflict] = useState(false);
  // the last save's refusal (a section may show it next to its field) until the next edit
  const [failure, setFailure] = useState<unknown>(null);
  const saved = base?.doc;
  const dirty = useMemo(() => !!draft && JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);
  // bumped by a save: the server's copy is adopted once it is the saved version (it may add what the server worked
  // out, such as a travel base's coordinates); the refetch can arrive before or after the save's own callback
  const [synced, setSynced] = useState(0);
  useEffect(() => {
    if (!latest || (dirty && base)) return;
    if (base && latestVersion < base.version) return; // the refetch after a save has not arrived yet
    setBase({ doc: structuredClone(latest), version: latestVersion });
    setDraft(structuredClone(latest));
    setConflict(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adopt the server's copy when it changes, not on each edit
  }, [latest, latestVersion, synced]);
  // a reload with unsaved rules asks first, as Branding and the editor do
  useLeaveGuard(dirty);
  // the user chose to see the other person's rules: this section's unsaved edits make way for them
  const reload = async () => {
    const fresh = (await settings.refetch()).data;
    if (!fresh) return;
    const doc = fresh.documents[kind] as Docs[K];
    setBase({ doc: structuredClone(doc), version: fresh.versions[kind] ?? 0 });
    setDraft(structuredClone(doc));
    setConflict(false);
  };
  const actions = (canManage: boolean, invalid = false) =>
    conflict ? (
      <>
        <span className="hint" role="status" style={{ marginInlineEnd: "auto" }}>{t("rules.conflict.title")} {t("rules.conflict.hint")}</span>
        <Button size="sm" icon={<RotateCcw />} onClick={() => void reload()}>
          {t("rules.conflict.reload")}
        </Button>
      </>
    ) : (
    <>
      <Button size="sm" variant="ghost" icon={<RotateCcw />} disabled={!dirty} onClick={() => saved && setDraft(structuredClone(saved))}>
        {t("rules.discard")}
      </Button>
      <Button
        size="sm"
        variant="primary"
        icon={<Save />}
        disabled={!dirty || !canManage || invalid}
        loading={save.isPending}
        onClick={() =>
          draft &&
          save.mutate(
            { kind, document: draft, baseVersion: base?.version },
            {
              onSuccess: (profile) => {
                // the saved copy is the base of the next save (the refetch then brings the same version)
                setBase({ doc: structuredClone(draft), version: profile.version });
                setSynced((n) => n + 1);
                setFailure(null);
                toast(t("rules.saved", { name: kindName(t, kind) }));
              },
              onError: (e) => (isSettingsConflict(e) ? setConflict(true) : (setFailure(e), toast(errorMessage(e), true))),
            },
          )
        }
      >
        {t("rules.action.save")}
      </Button>
    </>
    );
  const edit = (d: Docs[K]) => {
    setFailure(null);
    setDraft(d);
  };
  return { draft, setDraft: edit, dirty, actions, failure };
}

function Section({ title, description, footer, children }: { title: string; description?: string; footer?: ReactNode; children: ReactNode }) {
  return <Card title={title} description={description} footer={footer}>{children}</Card>;
}

// ------------------------------------------------------------------- roof
function RoofTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const setbacks = useDraft(orgId, docs, "setbacks");
  const roof = useDraft(orgId, docs, "roof_ai");
  const t = useT();
  const intl = useIntlShape();
  const s = setbacks.draft;
  const r = roof.draft;
  if (!s || !r) return null;
  const edge = (key: keyof S["SetbackEdges"], label: string) => (
    <Field label={label}>
      <NumberInput value={s.edges[key]} min={0} step={0.05} suffix="m" onChange={(v) => setbacks.setDraft({ ...s, edges: { ...s.edges, [key]: v ?? 0 } })} />
    </Field>
  );
  return (
    <div className="stack">
      <Section
        title={t("rules.setbacks.title")}
        description={t("rules.setbacks.description")}
        footer={setbacks.actions(canManage)}
      >
        <div className="stack">
          <SetbackPresetPicker orgId={orgId} current={s} onPick={(d) => setbacks.setDraft({ ...s, ...d })} disabled={!canManage} />
          <div className="form-grid-3">
            {edge("eave", t("rules.setbacks.edge.eave"))}
            {edge("ridge", t("rules.setbacks.edge.ridge"))}
            {edge("rake", t("rules.setbacks.edge.rake"))}
            {edge("hip", t("rules.setbacks.edge.hip"))}
            {edge("valley", t("rules.setbacks.edge.valley"))}
            {edge("step_flashing", t("rules.setbacks.edge.stepFlashing"))}
          </div>
          <div className="form-grid-3">
            <Field label={t("rules.setbacks.obstructions")}>
              <NumberInput value={s.obstruction_m} min={0} step={0.05} suffix="m" onChange={(v) => setbacks.setDraft({ ...s, obstruction_m: v ?? 0 })} />
            </Field>
            <Field label={t("rules.setbacks.pathways.label")} hint={t("rules.setbacks.pathways.hint")}>
              <NumberInput value={s.pathways.count} min={0} step={1} onChange={(v) => setbacks.setDraft({ ...s, pathways: { ...s.pathways, count: Math.round(v ?? 0) } })} />
            </Field>
            <Field label={t("rules.setbacks.pathwayWidth")}>
              <NumberInput value={s.pathways.width_m} min={0} step={0.05} suffix="m" onChange={(v) => setbacks.setDraft({ ...s, pathways: { ...s.pathways, width_m: v ?? 0 } })} />
            </Field>
          </div>
          {s.overrides.length > 0 && (
            <p className="hint">
              {t("rules.setbacks.overrides", { count: s.overrides.length, names: intl.formatList(s.overrides.map((o) => o.name), { type: "conjunction" }) })}
            </p>
          )}
        </div>
      </Section>
      <Section title={t("rules.roof.title")} description={t("rules.roof.description")} footer={roof.actions(canManage)}>
        <div className="form-grid">
          <Field label={t("rules.roof.material")}>
            <Select
              value={r.roof_defaults.material ?? "asphalt_shingle"}
              onChange={(v) => roof.setDraft({ ...r, roof_defaults: { ...r.roof_defaults, material: v } })}
              options={MATERIALS.map((m) => ({ value: m, label: materialName(t, m) }))}
            />
          </Field>
          <Field label={t("rules.roof.storeys")}>
            <NumberInput value={r.roof_defaults.storeys ?? null} min={1} max={20} step={1} onChange={(v) => roof.setDraft({ ...r, roof_defaults: { ...r.roof_defaults, storeys: v == null ? undefined : Math.round(v) } })} />
          </Field>
        </div>
      </Section>
    </div>
  );
}

// ----------------------------------------------------------------- module
function ModuleTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const layout = useDraft(orgId, docs, "layout");
  const t = useT();
  const l = layout.draft;
  if (!l) return null;
  const m = l.module;
  const f = l.flat_roof;
  const setModule = (patch: Partial<S["ModuleSpec"]>) => layout.setDraft({ ...l, module: { ...m, ...patch } });
  const setFlat = (patch: Partial<S["FlatRoofSettings"]>) => layout.setDraft({ ...l, flat_roof: { ...f, ...patch } });
  return (
    <div className="stack">
      <Section title={t("rules.panel.title")} description={t("rules.panel.description")} footer={layout.actions(canManage)}>
        <div className="stack">
          <div className="form-grid">
            <Field label={t("rules.panel.model")} className="span-2">
              <TextInput dir="auto" value={m.model} onChange={(e) => setModule({ model: e.target.value })} />
            </Field>
            <Field label={t("rules.panel.power")}>
              <NumberInput value={m.watts} min={1} step={5} suffix="W" onChange={(v) => setModule({ watts: v ?? m.watts })} />
            </Field>
            <Field label={t("rules.panel.orientation.label")}>
              <Select
                value={l.orientation}
                onChange={(v) => layout.setDraft({ ...l, orientation: v })}
                options={[
                  { value: "best", label: t("rules.panel.orientation.best") },
                  { value: "portrait", label: t("rules.panel.orientation.portrait") },
                  { value: "landscape", label: t("rules.panel.orientation.landscape") },
                ]}
              />
            </Field>
            <Field label={t("rules.panel.width")}>
              <NumberInput value={m.width_m} min={0.1} step={0.001} suffix="m" onChange={(v) => setModule({ width_m: v ?? m.width_m })} />
            </Field>
            <Field label={t("rules.panel.height")}>
              <NumberInput value={m.height_m} min={0.1} step={0.001} suffix="m" onChange={(v) => setModule({ height_m: v ?? m.height_m })} />
            </Field>
            <Field label={t("rules.panel.gap")}>
              <NumberInput value={l.gap_m} min={0} step={0.01} suffix="m" onChange={(v) => layout.setDraft({ ...l, gap_m: v ?? 0 })} />
            </Field>
            <Field label={t("rules.panel.minPerFace")}>
              <NumberInput value={l.min_modules_per_face} min={1} step={1} onChange={(v) => layout.setDraft({ ...l, min_modules_per_face: Math.max(1, Math.round(v ?? 1)) })} />
            </Field>
            <Field label={t("rules.panel.minSolarAccess.label")} hint={t("rules.panel.minSolarAccess.hint")}>
              <NumberInput value={Math.round(l.min_solar_access * 100)} min={0} max={100} step={1} suffix="%" onChange={(v) => layout.setDraft({ ...l, min_solar_access: (v ?? 0) / 100 })} />
            </Field>
            <Field label={t("rules.panel.maxPitch")}>
              <NumberInput value={l.max_pitch_deg} min={0} max={90} step={1} suffix="°" onChange={(v) => layout.setDraft({ ...l, max_pitch_deg: v ?? 0 })} />
            </Field>
          </div>
        </div>
      </Section>
      <Section title={t("rules.flat.title")} description={t("rules.flat.description")} footer={layout.actions(canManage)}>
        <div className="form-grid-3">
          <Field label={t("rules.flat.below")}>
            <NumberInput value={f.max_pitch_deg} min={0} step={1} suffix="°" onChange={(v) => setFlat({ max_pitch_deg: v ?? 0 })} />
          </Field>
          <Field label={t("rules.flat.mounting.label")}>
            <Select value={f.mounting} onChange={(v) => setFlat({ mounting: v })} options={[{ value: "ballast", label: t("rules.flat.mounting.ballast") }, { value: "flush", label: t("rules.flat.mounting.flush") }]} />
          </Field>
          <Field label={t("rules.flat.tilt")}>
            <NumberInput value={f.tilt_deg} min={0} max={60} step={1} suffix="°" onChange={(v) => setFlat({ tilt_deg: v ?? 0 })} />
          </Field>
          <Field label={t("rules.flat.azimuth.label")} hint={t("rules.flat.azimuth.hint")}>
            <NumberInput value={f.azimuth_deg} min={0} max={360} step={1} suffix="°" onChange={(v) => setFlat({ azimuth_deg: v ?? 180 })} />
          </Field>
          <Field label={t("rules.flat.rowSpacing.label")} hint={t("rules.flat.rowSpacing.hint")}>
            <NumberInput value={f.row_spacing_factor} min={0} step={0.05} onChange={(v) => setFlat({ row_spacing_factor: v ?? 1 })} />
          </Field>
          <Field label={t("rules.flat.minRowGap")}>
            <NumberInput value={f.min_row_gap_m ?? 0.33} min={0} step={0.01} suffix="m" onChange={(v) => setFlat({ min_row_gap_m: v ?? 0 })} />
          </Field>
          <div className="span-3">
            <Switch checked={f.align_to_building ?? true} onChange={(v) => setFlat({ align_to_building: v })} label={t("rules.flat.align")} />
          </div>
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------- battery & financing
const FINANCING_PRODUCTS = ["loan", "lease", "ppa"] as const;
/** A product's starting terms when the installer adds it (the rates are theirs to enter). */
const NEW_FINANCING = {
  loan: { apr: 0, term_years: 20, down_payment_pct: 0 },
  lease: { term_years: 20, rate_per_kwh: 0, escalator: 0 },
  ppa: { term_years: 20, rate_per_kwh: 0, escalator: 0 },
} satisfies Record<(typeof FINANCING_PRODUCTS)[number], unknown>;

function OfferTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const pricing = useDraft(orgId, docs, "pricing");
  const financial = useDraft(orgId, docs, "financial");
  const t = useT();
  const p = pricing.draft;
  const fin = financial.draft;
  if (!p || !fin) return null;
  const batteries = p.battery_options;
  const setBattery = (i: number, patch: Partial<S["BatteryOption"]>) =>
    pricing.setDraft({ ...p, battery_options: batteries.map((b, j) => (i === j ? { ...b, ...patch } : b)) });
  const f = fin.financing;
  const setFin = (patch: Partial<S["Financing"]>) => financial.setDraft({ ...fin, financing: { ...f, ...patch } });
  // a product with no terms is not offered: a new company offers none (cash only) until it adds one here
  const offered = FINANCING_PRODUCTS.filter((k) => f[k]);
  const missing = FINANCING_PRODUCTS.filter((k) => !f[k]);
  const addProduct = (k: (typeof FINANCING_PRODUCTS)[number]) => setFin({ [k]: NEW_FINANCING[k] });
  const removeProduct = (k: (typeof FINANCING_PRODUCTS)[number]) =>
    setFin({ [k]: null, ...(f.default_product === k ? { default_product: "cash" as const } : {}) });
  // battery options and travel are one pricing document: either section's Save saves both
  const travelIssues = travelProblems(p.travel && p.travel.enabled !== false ? p.travel : null);
  const pricingActions = pricing.actions(canManage, travelIssues.bases || travelIssues.bands || travelIssues.accommodation);
  return (
    <div className="stack">
      <Section title={t("rules.battery.title")} description={t("rules.battery.description", { currency: p.currency })} footer={pricingActions}>
        <div className="stack">
          {batteries.length === 0 && <p className="muted">{t("rules.battery.none")}</p>}
          {batteries.map((b, i) => (
            <div key={i} className="form-grid-3" style={{ alignItems: "end", paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
              <Field label={t("rules.battery.label")}>
                <TextInput dir="auto" value={b.label} onChange={(e) => setBattery(i, { label: e.target.value })} />
              </Field>
              <Field label={t("rules.battery.model")}>
                <TextInput dir="auto" value={b.model} onChange={(e) => setBattery(i, { model: e.target.value })} />
              </Field>
              <Field label={t("rules.battery.price")}>
                <NumberInput value={b.price} min={0} step={100} suffix={p.currency} onChange={(v) => setBattery(i, { price: v ?? 0 })} />
              </Field>
              <Field label={t("rules.battery.energy")}>
                <NumberInput value={b.energy_kwh} min={0.1} step={0.1} suffix="kWh" onChange={(v) => setBattery(i, { energy_kwh: v ?? b.energy_kwh })} />
              </Field>
              <Field label={t("rules.battery.power")}>
                <NumberInput value={b.power_kw} min={0.1} step={0.1} suffix="kW" onChange={(v) => setBattery(i, { power_kw: v ?? b.power_kw })} />
              </Field>
              <div>
                <Button variant="danger" icon={<Trash2 />} onClick={() => pricing.setDraft({ ...p, battery_options: batteries.filter((_, j) => j !== i) })}>
                  {t("rules.action.remove")}
                </Button>
              </div>
            </div>
          ))}
          <div>
            <Button
              icon={<Plus />}
              onClick={() =>
                pricing.setDraft({
                  ...p,
                  battery_options: [...batteries, { id: `battery-${batteries.length + 1}`, label: t("rules.battery.defaultLabel"), model: "", power_kw: 5, energy_kwh: 13.5, price: 12000 }],
                })
              }
            >
              {t("rules.battery.add")}
            </Button>
          </div>
        </div>
      </Section>
      <TravelSection pricing={p} setPricing={pricing.setDraft} footer={pricingActions} failure={pricing.failure} Section={Section} />
      <Section title={t("rules.financing.title")} description={t("rules.financing.description")} footer={financial.actions(canManage)}>
        <div className="stack">
          {offered.length === 0 && <p className="muted">{t("rules.financing.none")}</p>}
          {offered.length > 0 && (
            <Field label={t("rules.financing.default")}>
              <Select value={f.default_product} onChange={(v) => setFin({ default_product: v })} options={(["cash", ...offered] as const).map((v) => ({ value: v, label: t(`rules.financing.product.${v}`) }))} />
            </Field>
          )}
          {f.loan && (
            <div className="stack" style={{ gap: 10, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
              <div className="spread">
                <h3>{t("rules.financing.product.loan")}</h3>
                <Button variant="danger" icon={<Trash2 />} onClick={() => removeProduct("loan")}>{t("rules.action.remove")}</Button>
              </div>
              <div className="form-grid-3">
                <Field label={t("rules.financing.apr")}>
                  <NumberInput value={+(f.loan.apr * 100).toFixed(3)} min={0} max={100} step={0.1} suffix="%" onChange={(v) => setFin({ loan: { ...f.loan!, apr: (v ?? 0) / 100 } })} />
                </Field>
                <Field label={t("rules.financing.term")}>
                  <NumberInput value={f.loan.term_years} min={1} step={1} suffix={t("rules.unit.years")} onChange={(v) => setFin({ loan: { ...f.loan!, term_years: Math.max(1, Math.round(v ?? 1)) } })} />
                </Field>
                <Field label={t("rules.financing.downPayment")}>
                  <NumberInput value={f.loan.down_payment_pct} min={0} max={100} step={1} suffix="%" onChange={(v) => setFin({ loan: { ...f.loan!, down_payment_pct: v ?? 0 } })} />
                </Field>
              </div>
            </div>
          )}
          {(["lease", "ppa"] as const).map((k) => {
            const terms = f[k];
            if (!terms) return null;
            return (
              <div key={k} className="stack" style={{ gap: 10, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
                <div className="spread">
                  <h3>{t(`rules.financing.product.${k}`)}</h3>
                  <Button variant="danger" icon={<Trash2 />} onClick={() => removeProduct(k)}>{t("rules.action.remove")}</Button>
                </div>
                <div className="form-grid-3">
                  <Field label={t("rules.financing.ratePerKwh")}>
                    <NumberInput value={terms.rate_per_kwh} min={0} step={0.005} suffix={fin.currency} onChange={(v) => setFin({ [k]: { ...terms, rate_per_kwh: v ?? 0 } })} />
                  </Field>
                  <Field label={t("rules.financing.term")}>
                    <NumberInput value={terms.term_years} min={1} step={1} suffix={t("rules.unit.years")} onChange={(v) => setFin({ [k]: { ...terms, term_years: Math.max(1, Math.round(v ?? 1)) } })} />
                  </Field>
                  <Field label={t("rules.financing.escalator")}>
                    <NumberInput value={+(terms.escalator * 100).toFixed(3)} min={0} max={100} step={0.1} suffix="%" onChange={(v) => setFin({ [k]: { ...terms, escalator: (v ?? 0) / 100 } })} />
                  </Field>
                </div>
              </div>
            );
          })}
          {missing.length > 0 && (
            <div className="row">
              {missing.map((k) => (
                <Button key={k} icon={<Plus />} onClick={() => addProduct(k)}>{t(`rules.financing.add.${k}`)}</Button>
              ))}
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}

// ------------------------------------------------------------ service area
function AreaTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const area = useDraft(orgId, docs, "service_area");
  const ours = useBrandDefaultTexts(orgId).data;
  const t = useT();
  const a = area.draft;
  if (!a) return null;
  const messageMissing = !!ownTextError(a.outside_message);
  const unrestricted = !a.countries.length && !a.regions.length && !a.area;
  return (
    <div className="stack">
      <Section title={t("rules.area.title")} description={t("rules.area.description")} footer={area.actions(canManage, messageMissing)}>
        <div className="stack">
          {unrestricted && <Alert tone="info">{t("rules.area.everywhere")}</Alert>}
          <div className="form-grid">
            <Field label={t("rules.area.countries.label")} hint={t("rules.area.countries.hint", { examples: "US, CA" })}>
              <ChipsInput
                values={a.countries}
                onChange={(v) => area.setDraft({ ...a, countries: v })}
                // eslint-disable-next-line solaros-i18n/no-hardcoded-text -- an ISO country code, not words
                placeholder="US"
                normalize={(v) => v.trim().toUpperCase()}
                validate={(v) => (/^[A-Z]{2}$/.test(v) ? null : t("rules.area.countries.invalid", { code: v }))}
              />
            </Field>
            <Field label={t("rules.area.regions.label")} hint={t("rules.area.regions.hint", { examples: "US-CA, CA-BC" })}>
              <ChipsInput
                values={a.regions}
                onChange={(v) => area.setDraft({ ...a, regions: v })}
                // eslint-disable-next-line solaros-i18n/no-hardcoded-text -- an ISO 3166-2 code, not words
                placeholder="US-CA"
                normalize={(v) => v.trim().toUpperCase()}
                validate={(v) => (/^[A-Z]{2}-[A-Z0-9]{1,3}$/.test(v) ? null : t("rules.area.regions.invalid", { code: v, example: "US-CA" }))}
              />
            </Field>
          </div>
          <OwnTextField
            label={t("rules.area.outsideMessage")}
            multiline
            maxLength={500}
            value={a.outside_message}
            ours={ours?.outside_message}
            disabled={!canManage}
            onChange={(v) => area.setDraft({ ...a, outside_message: v })}
          />
          {a.area && <p className="hint">{t("rules.area.customMap")}</p>}
        </div>
      </Section>
      <Section
        title={t("rules.limit.title")}
        description={t("rules.limit.description")}
        footer={area.actions(canManage, messageMissing)}
      >
        <div className="form-grid" style={{ alignItems: "end" }}>
          <Field label={t("rules.limit.perDay")}>
            <NumberInput
              value={a.limits?.daily_new_designs ?? null}
              disabled={a.limits?.daily_new_designs == null}
              min={0}
              step={1}
              placeholder={t("rules.limit.unlimited")}
              onChange={(v) => area.setDraft({ ...a, limits: { ...a.limits, daily_new_designs: v == null ? 0 : Math.max(0, Math.round(v)) } })}
            />
          </Field>
          <div style={{ paddingBottom: 8 }}>
            <Switch
              checked={a.limits?.daily_new_designs == null}
              onChange={(unlimited) => area.setDraft({ ...a, limits: { ...a.limits, daily_new_designs: unlimited ? null : 25 } })}
              label={t("rules.limit.unlimited")}
            />
          </div>
          <Field label={t("rules.limit.perVisitor.label")} hint={t("rules.limit.perVisitor.hint", { count: 3 })}>
            <NumberInput
              value={a.limits?.per_visitor_daily_new_designs ?? null}
              disabled={a.limits != null && "per_visitor_daily_new_designs" in a.limits && a.limits.per_visitor_daily_new_designs == null}
              min={0}
              step={1}
              placeholder={a.limits != null && "per_visitor_daily_new_designs" in a.limits ? t("rules.limit.unlimited") : t("rules.limit.default", { count: 3 })}
              onChange={(v) => {
                const { per_visitor_daily_new_designs: _drop, ...rest } = a.limits ?? {};
                area.setDraft({ ...a, limits: v == null ? rest : { ...rest, per_visitor_daily_new_designs: Math.max(0, Math.round(v)) } });
              }}
            />
          </Field>
          <div style={{ paddingBottom: 8 }}>
            <Switch
              checked={a.limits != null && "per_visitor_daily_new_designs" in a.limits && a.limits.per_visitor_daily_new_designs == null}
              onChange={(unlimited) => {
                const { per_visitor_daily_new_designs: _drop, ...rest } = a.limits ?? {};
                area.setDraft({ ...a, limits: unlimited ? { ...rest, per_visitor_daily_new_designs: null } : rest });
              }}
              label={t("rules.limit.unlimited")}
            />
          </div>
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------- advanced
function AdvancedTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const [kind, setKind] = useState<SettingsKind>("pricing");
  const save = useSaveSettings(orgId);
  const toast = useToast();
  const t = useT();
  const settings = useEffectiveSettings(orgId);
  const original = JSON.stringify(docs[kind], null, 2);
  const version = settings.data?.versions[kind] ?? 0;
  const [text, setText] = useState(original);
  // the text as loaded and its version: a save made after someone else's is refused (409) instead of undoing it, and
  // newer settings from the server replace the text only while it has no unsaved edits (Discard loads them)
  const [loaded, setLoaded] = useState({ kind, text: original, version });
  const load = () => {
    setText(original);
    setLoaded({ kind, text: original, version });
  };
  useEffect(() => {
    if (text === loaded.text || kind !== loaded.kind) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adopt the server's copy when it changes, not on each edit
  }, [kind, original, version]);
  let parseError: string | null = null;
  try {
    JSON.parse(text);
  } catch (e) {
    parseError = (e as Error).message;
  }
  return (
    <Card
      title={t("rules.advanced.title")}
      description={t("rules.advanced.description")}
      actions={
        <div style={{ width: 200 }}>
          <Select ariaLabel={t("rules.advanced.document")} value={kind} onChange={setKind} options={KINDS.map((k) => ({ value: k, label: kindName(t, k) }))} />
        </div>
      }
      footer={
        <>
          <Button size="sm" variant="ghost" icon={<RotateCcw />} disabled={text === original && version === loaded.version} onClick={load}>
            {t("rules.discard")}
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={<Save />}
            disabled={!canManage || text === loaded.text || !!parseError}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { kind, document: JSON.parse(text), baseVersion: loaded.version },
                {
                  onSuccess: () => toast(t("rules.saved", { name: kindName(t, kind) })),
                  // Discard then loads their version
                  onError: (e) => (isSettingsConflict(e) ? (toast(t("rules.conflict.title"), true), void settings.refetch()) : toast(errorMessage(e), true)),
                },
              )
            }
          >
            {t("rules.advanced.save", { name: kindName(t, kind) })}
          </Button>
        </>
      }
    >
      <div className="stack">
        <textarea className={cx("textarea", "code")} dir="ltr" spellCheck={false} aria-label={t("rules.advanced.title")} value={text} onChange={(e) => setText(e.target.value)} />
        {parseError && <span className="field-error">{t("rules.advanced.parseError", { detail: parseError })}</span>}
      </div>
    </Card>
  );
}

export function Rules() {
  const orgId = useOrgId();
  const { can } = useOrg();
  const canManage = can("settings_rules", "edit");
  const settings = useEffectiveSettings(orgId);
  const [tab, setTab] = useState<Tab>("roof");
  const t = useT();
  if (settings.isLoading) return <Spinner />;
  if (settings.error || !settings.data) return <Alert tone="danger">{errorMessage(settings.error)}</Alert>;
  const docs = settings.data.documents;
  const tabs: { id: Tab; label: MessageKey }[] = [
    { id: "roof", label: "rules.tab.roof" },
    { id: "module", label: "rules.tab.module" },
    { id: "offer", label: "rules.tab.offer" },
    { id: "area", label: "rules.tab.area" },
    { id: "advanced", label: "rules.tab.advanced" },
  ];
  const props = { orgId, docs, canManage };
  return (
    <>
      <PageHead
        title={t("rules.page.title")}
        description={t("rules.page.description")}
        actions={settings.data.pins ? <span className="badge plain" title={t("rules.page.preset")}>{Object.values(settings.data.pins)[0]?.ref.split(" ")[0]}</span> : null}
      />
      {!canManage && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="info">{t("rules.page.readOnly")}</Alert>
        </div>
      )}
      <div className="tabs" role="tablist" aria-label={t("rules.page.title")}>
        {tabs.map((b) => (
          <button key={b.id} className={cx(tab === b.id && "on")} onClick={() => setTab(b.id)} role="tab" aria-selected={tab === b.id}>
            {b.id === "advanced" && <Braces size={13} style={{ verticalAlign: -2, marginInlineEnd: 4 }} />}
            {t(b.label)}
          </button>
        ))}
      </div>
      {tab === "roof" && <RoofTab {...props} />}
      {tab === "module" && <ModuleTab {...props} />}
      {tab === "offer" && <OfferTab {...props} />}
      {tab === "area" && <AreaTab {...props} />}
      {tab === "advanced" && <AdvancedTab {...props} />}
    </>
  );
}
